<template>
  <v-card
    ref="viewSection"
    class="mx-auto"
    color="transparent"
    elevation="0"
    :data-active-content-type="activeContentType"
  >
    <FacetsToolbar
      :config="config"
      :loading="loading"
      :focus-mode-toolbar-top="focusModeToolbarTop"
    />

    <section
      class="my-8 my-lg-16 px-4 d-flex pa-0 col-12"
      :class="{ 'mt-12' : focusMode }"
      style="position: relative;"
    >
      <Facets
        v-if="$vuetify.breakpoint.lgAndUp"
        :filters-config="getFiltersConfig"
        :hide-sidebar.sync="hideSidebar"
      />
      <section
        v-if="$vuetify.breakpoint.lgAndUp"
        class="d-flex justify-center px-0"
        :class="{'ml-8' : !hideSidebar}"
      >
        <v-btn
          absolute
          fab
          top
          dark
          x-small
          color="primary"
          style="z-index: 1;"
          @click="changeSideBar()"
        >
          <v-icon dark>
            {{ hideSidebar ? "mdi-forwardburger" : "mdi-backburger" }}
          </v-icon>
        </v-btn>
        <v-divider
          class="fill-height"
          inset
          vertical
        />
      </section>

      <div
        class="content flex-wrap col pa-0 overflow-x-auto"
        :class="$vuetify.breakpoint.lgAndUp ? 'ml-6' : 'ml-0'"
      >
        <DataTable
          v-if="$route.meta.listComponent === 'DataTable'"
          :page.sync="page"
          :sort-config="sort"
          :show-list="getTableItems"
          :page-count="pageCount"
          :content-type-label="contentTypeLabel"
          :config="config"
          :items-per-page="(config.views?.list.itemsPerPage || 1)"
          @row-click="handleRowClick"
        >
          <template #top>
            <ListToolbar
              :total="getTotal"
              :actions="config.views?.list.actions.top ? config.views.list.actions.top.filter(shouldShowAction) : []"
              :create-permission="`CREATE_${contentWtype}`"
              @action-click="handleAction"
            />
          </template>
        </DataTable>
        <DataIterator
          v-else-if="$route.meta.listComponent === 'DataIterable'"
          :table-config="config"
          :actions-config="config"
          :list="getTableItems"
          :items-per-page="(config.views?.list.itemsPerPage || 1)"
        >
          <template #top>
            <section
              v-if="activeWorkspace !== 'spontaneous' && offer.uuid === $route.params.offerUuid"
              class="
                d-flex align-center
                py-0 pb-6 px-0 col-12
              "
              style="gap: 8px;"
            >
              <v-btn small icon fab class="px-0" text :to="getBackRoute()">
                <v-icon>mdi-chevron-left</v-icon>
              </v-btn>
              <h3 class="text-md-h6 font-weight-bold">{{ offer?.name || 'Candidatures' }}</h3>
            </section>

            <ListToolbar
              :total="getTotal"
              :actions="config.views?.list.actions.top ? config.views.list.actions.top.filter(shouldShowAction) : []"
              :create-permission="`CREATE_${contentWtype}`"
              @action-click="handleAction"
            />
          </template>
        </DataIterator>

        <v-pagination v-model="page" :disabled="!canPerformAction(`VIEW_${contentWtype}`) || pageCount <= 1" :length="pageCount" total-visible="5" class="py-6" />
      </div>
    </section>

    <v-dialog
      :value="dialog.show"
      persistent
      max-width="500"
    >
      <ReopenOffer
        v-if="dialog.component === 'ReopenOffer'"
        :item="offer"
        @close-dialog="dialog.show = false"
        @handle-loading="loading.form = $event"
      />
      <ConvertModelOfferToOffer
        v-else-if="dialog.component === 'ConvertModelOfferToOffer'"
        :item="offer"
        @close-dialog="dialog.show = false"
        @handle-loading="loading.form = $event"
      />
      <MassRejectApplications
        v-else-if="dialog.component === 'MassRejectApplications'"
        :item="offer"
        :offer-uuid="$route.params.offerUuid"
        @close-dialog="dialog.show = false"
        @handle-loading="loading.form = $event"
      />
    </v-dialog>
  </v-card>
</template>

<script>
import { mapActions, mapMutations, mapGetters, mapState } from 'vuex';

import FacetsToolbar from '@/components/dynamic/list/components/FacetsToolbar.vue';
import Facets from '@/components/Facets.vue';
import DataTable from '@/components/dataTable/DataTable.vue';
import DataIterator from '@/components/table/DataIterator.vue';
import ReopenOffer from '@/components/application/components/ReopenOffer.vue';
import MassRejectApplications from '@/components/application/components/MassRejectApplications.vue';
import ListToolbar from '@/components/dynamic/list/components/ListToolbar.vue';
import ConvertModelOfferToOffer from '@/components/centralizedOffer/ConvertModelOfferToOffer.vue';

export default {
  name: 'GenericListing',
  components: {
    FacetsToolbar,
    Facets,
    DataTable,
    DataIterator,
    ReopenOffer,
    MassRejectApplications,
    ConvertModelOfferToOffer,
    ListToolbar
  },
  props: {
    config: {
      type: Object,
      default: () => ({})
    },
    contentTypeLabel: {
      type: String,
      default: null
    }
  },
  data () {
    return {
      hideSidebar: false,
      loading: {
        skeleton: false,
        form: false
      },
      dialog: {
        show: false,
        component: null
      }
    };
  },
  computed: {
    ...mapState(['focusMode', 'appliedFilters', 'sort', 'offer', 'activeWorkspace', 'activeContentType']),
    ...mapGetters(['getTableItems', 'getFiltersConfig', 'getTotal', 'getSortValueByKey', 'getDashboardConfigurationByLabel', 'canPerformAction']),
    page: {
      get () {
        return this.$store.state.page;
      },
      set (value) {
        this.setPage(Number(value));
        this.scrollToField('viewSection');
      }
    },
    pageCount () {
      return Math.ceil(this.getTotal / (this.config.views?.list.itemsPerPage || 1));
    },
    contentWtype () {
      const { wtype } = this.getDashboardConfigurationByLabel(this.contentTypeLabel);
      return wtype;
    },
    isOfferValid () {
      if (!this.offer) return false;

      const recruiterFields = this.$store.state.activeBoardConfiguration?.inputs;
      const fields = this.$store.state.rawContentTypes?.Offer?.fields;

      if (!fields || !recruiterFields) return false;

      return Object.entries(fields).every(([key, field]) => {
        const isRecruiterField = recruiterFields.find(input => input.name === key);

        if (!isRecruiterField || (!field.mandatory && !field.required)) {
          return true;
        }

        const value = this.offer[key];

        // Check for null or undefined.
        if ([null, undefined].includes(value)) return false;
        // Check for empty strings.
        if (typeof value === 'string' && value.trim() === '') return false;
        // Check for empty arrays.
        if (Array.isArray(value)) return value.length > 0;
        // Check for empty objects.
        if (typeof value === 'object' && Object.keys(value).length === 0) return false;

        return true;
      });
    },
    focusModeToolbarTop () {
      if (this.$vuetify.breakpoint.xsOnly) {
        return '64px';
      }
      return this.focusMode ? '24px' : '88px';
    }
  },
  updated () {
    this.setItemsByPage(this.config.views?.list.itemsPerPage || 1);
  },
  methods: {
    ...mapActions(['setPage', 'searchData', 'clearFiltersAndSearch', 'removeFilterAndSearch', 'setSort']),
    ...mapMutations(['setItemsByPage']),
    findFilterConfigByComponent (attribute) {
      return this.config.filters.find(facet => facet.attribute === attribute);
    },
    async getData () {
      this.searchData();
    },
    changeSideBar () {
      this.hideSidebar = !this.hideSidebar;
    },
    scrollToField (field) {
      this.$vuetify.goTo(this.$refs[field], {
        duration: 500,
        offset: 64 + 16,
        easing: 'easeInOutCubic'
      });
    },
    handleRowClick (item) {
      if (!item?.wtype) return;

      switch (item.wtype) {
        case 'offer':
          this.$router.push({
            name: 'offer-candidatures',
            params: { offerUuid: item.uuid }
          });
          break;
        default:
          console.info('Row Clicked');
      }
    },
    async handleAction (action) {
      switch (action.mode) {
        case 'creer': {
          if (this.canPerformAction(`CREATE_${this.contentWtype}`)) {
            this.$router.push({ name: this.contentWtype, params: { contentTypeLabel: this.contentTypeLabel, mode: action.mode } });
          }
          break;
        }
        case 'find-candidate': {
          if (this.canPerformAction(`VIEW_${this.contentWtype}`)) {
            this.$router.push({ name: 'rechercher-candidate', params: { offerUuid: this.$route.params.offerUuid } });
          }
          break;
        }
        case 'reopen-offer': {
          if (this.canPerformAction('EDIT_OFFER')) {
            this.dialog.show = true;
            this.dialog.component = 'ReopenOffer';
          }
          break;
        }
        case 'mass-reject': {
          if (this.canPerformAction('EDIT_APPLICATION')) {
            this.dialog.show = true;
            this.dialog.component = 'MassRejectApplications';
          }
          break;
        }
        case 'refresh': {
          this.searchData();
          break;
        }
        case 'convert-model-offer-to-offer': {
          this.dialog.show = true;
          this.dialog.component = 'ConvertModelOfferToOffer';
          break;
        }
        default: {
          console.log('Action Clicked', action);
        }
      }
    },
    shouldShowAction (action) {
      // If workspace is spontaneous, don't allow these two actions
      if (this.activeWorkspace === 'spontaneous' && ['find-candidate', 'mass-reject', 'reopen-offer'].includes(action.mode)) {
        return false;
      }

      const currentRouteName = this.$router.currentRoute.name;
      // If route is candidatures, don't allow mass-reject
      if (currentRouteName === 'candidatures' && action.mode === 'mass-reject') {
        return false;
      }

      if (currentRouteName === 'offer-candidatures') {
        switch (action.mode) {
          case 'reopen-offer':
            if (!this.isOfferValid) return false;
            return this.offer.status === 'draft';
          case 'mass-reject': {
            return this.offer.status === 'published';
          }
          default:
            return true;
        }
      }

      return true;
    },
    getBackRoute () {
      if (['offer-candidatures', 'offers'].includes(this.$router.currentRoute.name)) {
        return {
          name: 'offers',
          params: { contentTypeLabel: 'offres' }
        };
      }

      return { name: 'home' };
    }
  }
};
</script>

<style scoped>
[data-active-content-type="application"] {
  @container actions-container (max-width: 1440px) {
    :deep(.desktop-actions) {
      display: none;
    }

    :deep(.mobile-actions) {
      display: block;
    }
  }
}
[data-active-content-type="job"], [data-active-content-type="model_offer"] {
  @container actions-container (max-width: 1170px) {
    :deep(.desktop-actions) {
      display: none;
    }

    :deep(.mobile-actions) {
      display: block;
    }
  }
}
[data-active-content-type="offer"] {
  @container actions-container (max-width: 520px) {
    :deep(.desktop-actions) {
      display: none;
    }

    :deep(.mobile-actions) {
      display: block;
    }
  }
}
</style>
